import CanvasExifOrientation from "canvas-exif-orientation";
import { rbgEditorApplyBlurV100 } from "kaleido-event-tracker";
import { BrowserCompatibility, EXIF } from "../remove-bg-tools";
import { SVG } from "@svgdotjs/svg.js";
import "@svgdotjs/svg.filter.js";

export default class Editor {
  constructor(opts) {
    this.id = opts.id;
    this.target = opts.target;
    this.original_url = opts.original_url;
    this.foreground_url = opts.foreground_url;
    this.svg = null;
    this.maskDrawing = null;
    this.originalMask = null;
    this.foregroundMask = null;
    this.mousedown = false;
    this.path = null;
    this.lastPoint = null;
    this.lastChange = null;
    this.paths = [];
    this.listeners = { change: [], pathStopped: [] };
    this.width = null;
    this.height = null;
    this.zoom = 1.0;
    this.mode = null;
    this.brushOffset = 0;
    this.redoStack = [];

    this.original = null;
    this.foreground = null;
    this.foregroundThumbnail = null;
    this.foregroundThumbnailMinSize = [156, 156];

    this.setBrushSize(32);

    this.detectObjectsEnabled = null;
    this.foregroundBlob = null;

    this.lastBackgroundOpts = null;
  }

  render() {
    let that = this;
    return new Promise((resolve, reject) => {
      this.getImageLayers()
        .then((layers) => {
          this.width = layers.width;
          this.height = layers.height;
          this.svg = SVG().addTo(that.target).size(that.width, that.height).viewbox(0, 0, layers.width, layers.height);

          this.backgroundWrapper = this.svg.group();
          this.background = this.svg.group();
          this.backgroundWrapper.add(this.background);

          this.pointHelper = this.svg.node.createSVGPoint();

          this.foregroundWrapper = this.svg.group();

          this.foreground = this.svg
            .image(layers.foregroundURL, () => {
              // TODO: url.revokeForegroundURL();
            })
            .size(this.width, this.height);

          this.foregroundWrapper.add(this.foreground);

          this.foregroundThumbnail = this.svg
            .image(layers.thumbnailURL, () => {
              // TODO: url.revokeThumbnailURL();
            })
            .size(this.width, this.height);
          this.foregroundThumbnail.remove();

          this.original = this.svg
            .image(layers.originalURL, () => {
              // TODO: url.revokeOriginalURL();
            })
            .size(this.width, this.height);

          this.maskDrawing = this.svg.group();

          this.foregroundMask = this.svg.mask();
          var foregroundMaskBase = this.svg.rect(this.width, this.height).fill("#fff");
          this.foregroundMask.add(foregroundMaskBase);
          this.foregroundMask.add(this.maskDrawing);
          this.foregroundWrapper.maskWith(this.foregroundMask);

          this.originalMask = this.svg.mask();
          var originalMaskBase = this.svg.rect(this.width, this.height).fill("#000");
          this.originalMask.add(originalMaskBase);
          this.originalMask.use(this.maskDrawing);
          this.original.maskWith(this.originalMask);

          this.listenToEvents();

          resolve(this);
        })
        .catch(reject);
    });
  }

  on(event, handler) {
    this.listeners[event].push(handler);
  }

  trigger(event) {
    for (var callback of this.listeners[event]) {
      callback();
    }
  }

  setZoom(level, opts = {}) {
    this.zoom = level;
    this.svg.node.style.transition = opts.animate ? "width 200ms, height 200ms" : "";
    this.svg.node.style.width = this.width * level + "px";
    this.svg.node.style.height = this.height * level + "px";
    return [this.width * level, this.height * level];
  }

  getCoords(e) {
    var coords = e.touches ? [e.touches[0].clientX, e.touches[0].clientY] : [e.clientX, e.clientY];
    this.pointHelper.x = coords[0];
    this.pointHelper.y = coords[1];

    this.pointHelper.y -= this.brushOffset;

    return this.pointHelper.matrixTransform(this.transformMatrix);
  }

  listenToEvents() {
    this.svg.on(
      "mousedown touchstart",
      (e) => {
        this.onMouseDown(e);
      },
      false
    );
    this.svg.on(
      "mousemove touchmove",
      (e) => {
        this.onMouseMove(e);
      },
      false
    );
    this.svg.on(
      "mouseup touchend",
      (e) => {
        this.onMouseUp(e);
      },
      false
    );
    this.svg.on(
      "mouseleave",
      (e) => {
        this.onMouseLeave(e);
      },
      false
    );
  }

  onMouseDown(e) {
    if (e.touches && e.touches.length >= 2) return;
    if (this.mode != "erase" && this.mode != "restore") return;
    // prevent drag
    if (e.preventDefault) {
      e.preventDefault();
    } else {
      e.returnValue = false;
    }
    if (this.mousedown) {
      this.stopPath();
    }
    this.startPath(e);
  }

  onMouseMove(e) {
    if (this.mode != "erase" && this.mode != "restore") return;
    if (!this.mousedown && e.buttons > 0) {
      this.startPath(e);
    }
    if (this.mousedown) {
      var coords = this.getCoords(e);
      var diffx = this.lastPoint.x - coords.x;
      var diffy = this.lastPoint.y - coords.y;
      var diff = Math.sqrt(Math.pow(diffx, 2) + Math.pow(diffy, 2)) * this.zoom;
      var now = new Date();
      if (diff > 5 || diff > this.brushSize * 0.5 || (diff > 0 && now - this.lastChange > 200)) {
        var arr = this.path.array().valueOf();
        arr.push([coords.x, coords.y]);
        this.path.plot(arr);
        this.lastPoint = coords;
        this.lastChange = now;
      }
    }
  }

  onMouseUp(e) {
    this.stopPath();
  }

  onMouseLeave(e) {
    if (this.mousedown) {
      this.stopPath();
    }
  }

  cancelPath() {
    if (!this.mousedown) return;
    this.path.remove();
    this.path = null;
    this.mousedown = false;
  }

  stopPath() {
    if (!this.mousedown) return;
    this.mousedown = false;
    this.clearRedoStack();
    this.paths.push(this.path);
    this.path = null;
    this.trigger("change");
    this.trigger("pathStopped");
  }

  canUndo() {
    return this.paths.length > 0;
  }

  undo() {
    if (!this.canUndo()) return;
    var path = this.paths.pop();
    path.remove();
    this.redoStack.push(path);
  }

  canRedo() {
    return this.redoStack.length > 0;
  }

  redo() {
    if (!this.canRedo()) return;
    var path = this.redoStack.pop();
    this.maskDrawing.add(path);
    this.paths.push(path);
  }

  clearRedoStack() {
    if (this.redoStack.length == 0) return;
    this.redoStack = [];
  }

  startPath(e) {
    this.mousedown = true;

    this.path = this.svg
      .polyline()
      .fill("none")
      .stroke({
        width: this.brushSize,
        color: this.mode == "erase" ? "#000000" : "#ffffff",
      })
      .attr({
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
      });
    this.maskDrawing.add(this.path);
    this.transformMatrix = this.path.node.getScreenCTM().inverse();
    this.lastPoint = this.getCoords(e);
    this.lastChange = new Date();

    var arr = this.path.array().valueOf();
    arr.pop();
    arr.push([this.lastPoint.x, this.lastPoint.y]);
    arr.push([this.lastPoint.x + 1, this.lastPoint.y]);
    this.path.plot(arr);
  }

  setMode(mode) {
    this.mode = mode;
  }

  setDetectObjectsEnabled(detectObjectsEnabled) {
    this.detectObjectsEnabled = detectObjectsEnabled;
  }

  getMode() {
    return this.mode;
  }

  setBrushSize(size) {
    this.brushSize = size;
  }

  setBrushOffset(offset) {
    this.brushOffset = offset;
  }

  getImageLayers() {
    return new Promise((resolve, reject) => {
      Promise.all([this.getOriginal(), this.getForeground()])
        .then((images) => {
          var width = images[1].width;
          var height = images[1].height;
          this.loadToCanvas(...images).then(({ original, foreground }) => {
            var thumbnailSize = [width, height];
            if (thumbnailSize[0] > thumbnailSize[1]) {
              var ratio = this.foregroundThumbnailMinSize[0] / thumbnailSize[0];
              thumbnailSize[0] = this.foregroundThumbnailMinSize[0];
              thumbnailSize[1] *= ratio;
            } else {
              var ratio = this.foregroundThumbnailMinSize[1] / thumbnailSize[1];
              thumbnailSize[0] *= ratio;
              thumbnailSize[1] = this.foregroundThumbnailMinSize[1];
            }

            var thumbnail = this.createCanvas(thumbnailSize[0], thumbnailSize[1]);
            thumbnail.ctx.drawImage(foreground, 0, 0, thumbnailSize[0], thumbnailSize[1]);

            Promise.all([this.canvasToUrl(foreground), this.canvasToUrl(original), this.canvasToUrl(thumbnail.canvas)])
              .then((urlResults) => {
                resolve({
                  width: width,
                  height: height,
                  foregroundURL: urlResults[0].url,
                  revokeForegroundURL: urlResults[0].revokeURL,
                  originalURL: urlResults[1].url,
                  revokeOriginalURL: urlResults[1].revokeURL,
                  thumbnailURL: urlResults[2].url,
                  revokeThumbnailURL: urlResults[2].revokeURL,
                });
              })
              .catch((e) => {
                reject(e);
              });
          });
        })
        .catch((e) => {
          reject(e);
        });
    });
  }

  getEXIFOrientation(image) {
    return new Promise((resolve, reject) => {
      EXIF.getData(image, () => {
        resolve(image.exifdata.Orientation ? image.exifdata.Orientation : 1);
      });
    });
  }

  loadToCanvas(original, foreground) {
    return new Promise((resolve, reject) => {
      this.getEXIFOrientation(original)
        .then((orientation) => {
          var originalCanvas = this.getImageCanvas(original, {
            width: foreground.width,
            height: foreground.height,
            orientation: orientation,
            imageSmoothingQuality: "high",
          }).canvas;
          var foregroundCanvas = this.getImageCanvas(foreground).canvas;

          resolve({
            original: originalCanvas,
            foreground: foregroundCanvas,
          });
        })
        .catch((e) => {
          console.error(e);
        });
    });
  }

  canvasToUrl(canvas) {
    return new Promise((resolve, reject) => {
      // We can't use toBlob here because this would lead to blob being embedded in the SVG.
      // Blob URLs as part of the SVG are not a problem per se, but when the SVG is then place
      // in and <img src='...'> tag later on, the blob:-URLs are considered external resources
      // (at least in Chrome as of this writing), which results in them loading any more. As a
      // result the final exported PNG file would just be broken (large "missing image" icon).
      var url = canvas.toDataURL("image/png");
      resolve({
        url: url,
        revokeURL: () => {},
      });
    });
  }

  createCanvas(width, height) {
    var canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    var ctx = canvas.getContext("2d");
    return { canvas: canvas, ctx: ctx };
  }

  getImageCanvas(img, opts = {}) {
    if (!opts.width) opts.width = img.width;
    if (!opts.height) opts.height = img.height;
    if (!opts.orientation) opts.orientation = 1;

    var baseImage = img;
    if (opts.orientation != 1 && !BrowserCompatibility.supportsNativeExif(navigator.userAgent)) {
      baseImage = CanvasExifOrientation.drawImage(img, opts.orientation);
    }

    var { canvas, ctx } = this.createCanvas(opts.width, opts.height);
    if (opts.imageSmoothingQuality) ctx.imageSmoothingQuality = opts.imageSmoothingQuality;
    ctx.drawImage(baseImage, 0, 0, opts.width, opts.height);
    return { canvas, ctx, width: opts.width, height: opts.height };
  }

  getOriginal() {
    return this.loadImage(this.original_url);
  }

  getForeground() {
    return this.foregroundBlob ? this.loadImage(URL.createObjectURL(this.foregroundBlob)) : this.loadImage(this.foreground_url);
  }

  setBackground(opts = false, callback) {
    this.lastBackgroundOpts = opts;
    if (this.background) this.background.clear();
    if (opts) {
      if (opts.color) {
        this.background.rect(this.width, this.height).fill(opts.color);
        this.trigger("change");
        if (callback) callback();
      } else if (opts.image_url) {
        this.loadImage(opts.image_url)
          .then((img) => {
            this.getEXIFOrientation(img).then((orientation) => {
              var canvas, ctx;
              if (BrowserCompatibility.supportsNativeExif(navigator.userAgent)) {
                canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, img.width, img.height);
              } else {
                canvas = CanvasExifOrientation.drawImage(img, orientation);
                ctx = canvas.getContext("2d");
              }

              var width = canvas.width;
              var height = canvas.height;

              this.canvasToUrl(canvas)
                .then((url) => {
                  var aspectRatio = width / height;
                  var svgAspectRatio = this.width / this.height;
                  var scale = aspectRatio >= svgAspectRatio ? height / this.height : width / this.width;
                  width /= scale;
                  height /= scale;
                  var bgImage = this.background
                    .image(url.url, () => {
                      this.backgroundImage = bgImage;
                      this.backgroundImage.filterWith(function (add) {
                        if (!add) return;
                        add.gaussianBlur(0);
                      });
                      this.backgroundFilter = this.backgroundImage.filterer();
                      this.applyBackgroundBlur();
                      this.trigger("change");
                      if (callback) callback();
                    })
                    .size(width, height)
                    .hide();
                })
                .catch((e) => {
                  console.error(e);
                });
            });
          })
          .catch((e) => {
            console.error(e);
          });
      } else {
        if (callback) callback();
      }
    } else {
      if (callback) callback();
    }
  }

  setBackgroundBlur(value) {
    this.backgroundBlur = value;
    rbgEditorApplyBlurV100({ image_id: this.id, blur_value: value });
    if (this.backgroundImage) {
      this.applyBackgroundBlur();
      this.trigger("change");
    }
  }

  applyBackgroundBlur() {
    if (this.backgroundImage) {
      var w = this.backgroundImage.width();
      var h = this.backgroundImage.height();
      var smaller = w > h ? h : w;
      this.backgroundImage.show();
      this.backgroundImage.transform({ scale: 1.0 + (3 * this.backgroundBlur) / smaller });
      this.backgroundImage.translate((this.width - w) / 2, (this.height - h) / 2);
      if (this.backgroundFilter) {
        this.backgroundFilter.gaussianBlur(this.backgroundBlur);
      }
    }
  }

  loadImage(url) {
    return new Promise((resolve, reject) => {
      var img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = (e) => {
        resolve(img);
      };
      img.onerror = (e) => {
        reject("Image download failed.");
      };

      img.src = url;
    });
  }

  removeSvg() {
    this.svg.clear();
    this.svg.node.parentNode.removeChild(this.svg.node);
    this.svg = null;
  }

  toUrl(opts = {}) {
    return new Promise((resolve, reject) => {
      if (opts.hideBackground) this.background.remove();

      if (opts.thumbnail) {
        this.foreground.remove();
        this.foregroundThumbnail.addTo(this.foregroundWrapper);
      }

      if (opts.blob) {
        this.foregroundBlob = opts.blob;
        this.removeSvg();
        this.render().then(() => {
          if (opts.hideBackground) {
            // need to set background after render to not destroy thumbnail
            this.afterRender(
              opts,
              (params) => {
                this.setBackground(this.lastBackgroundOpts, () => {
                  resolve(params);
                });
              },
              reject
            );
          } else {
            this.setBackground(this.lastBackgroundOpts, () => {
              this.afterRender(
                opts,
                (params) => {
                  params.setBackground = () => {
                    this.setBackground(this.lastBackgroundOpts);
                  }; // need to execute after render in editor_layout
                  resolve(params);
                },
                reject
              );
            });
          }
        });
      } else {
        this.afterRender(opts, resolve, reject);
      }
    });
  }

  afterRender(opts, resolve, reject) {
    // setting the zoom to 100% temporily has shown to be necessary for mobile safari
    // because otherwise it exports it with the current zoom level applied
    var currentZoom = this.zoom;
    this.setZoom(1.0);

    var xml = new XMLSerializer().serializeToString(this.svg.node);
    this.setZoom(currentZoom);

    if (opts.hideBackground) this.background.addTo(this.backgroundWrapper);

    if (opts.thumbnail) {
      this.foregroundThumbnail.remove();
      this.foreground.addTo(this.foregroundWrapper);
    }

    var doctype = '<?xml version="1.0" standalone="no"?>' + '<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">';

    var ua = navigator.userAgent.toLowerCase();
    var isSafari = ua.indexOf("safari") >= 0 && ua.indexOf("chrome") < 0;
    var isEdge = /Edge\/\d./i.test(navigator.userAgent);
    var isUCBrowser = navigator.userAgent.indexOf(" UCBrowser/") >= 0;

    // Previously, we did this – but it looks like this is way slower. For instance, a 25mp image just crashes in chrome.
    // var svgBlob = new Blob([ doctype + xml ], { type: 'image/svg+xml' });
    // var svgUrl = window.URL.createObjectURL(svgBlob);
    var svgUrl = "data:image/svg+xml;base64," + btoa(doctype + xml);

    this.loadImage(svgUrl)
      .then((img) => {
        var { canvas, ctx } = this.createCanvas(this.width, this.height);

        if (isEdge) {
          $(img).css({ position: "fixed", top: "99vh", left: 0, zIndex: 25000, opacity: 0.01 }).appendTo($("body"));
          $(canvas).css({ position: "fixed", top: "99vh", left: 0, zIndex: 25000, opacity: 0.01 }).appendTo($("body"));
        }

        // settimeout necessary for edge 16-18, which otherwise render the image without mask sometimes?
        setTimeout(
          () => {
            ctx.drawImage(img, 0, 0);

            if (isEdge) {
              $(img).remove();
              $(canvas).remove();
            }

            if (canvas.toBlob && !isSafari && !isUCBrowser) {
              canvas.toBlob((blob) => {
                var url = URL.createObjectURL(blob);
                resolve({
                  url: url,
                  blob: blob,
                  width: this.width,
                  height: this.height,
                  destroy: () => {
                    URL.revokeObjectURL(url);
                  },
                });
              });
            } else {
              var url = canvas.toDataURL("image/png");
              resolve({
                url: url,
                width: this.width,
                height: this.height,
                destroy: () => {},
              });
            }
          },
          isEdge ? 400 : 0
        );
      })
      .catch((e) => {
        reject(e);
      });
  }

  destroy() {
    this.target.innerHTML = "";
    this.svg = null;
  }
}
